import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../shared/redux/hooks'
import Panel from '../../components/Panel'
import Table from '../../components/Table/Table'
import QueryError from '../conductor/QueryError'
import { getAdminUtilitiesApi } from '../../apis/apis'
import type { TariffItem, TariffStatus, Utility } from '../../apis/types'
import Layout from '../programs/Layout'
import { getTariffs } from './Tariffs.slice'
import { LIST_SEARCH_KEY, TariffPageHeader } from './components/TariffPageHeader'
import { TariffFilterBar, UTILITY_TYPE_HINTS, type UtilityTypeFilter } from './components/TariffFilterBar'
import { TariffTypeChip, TariffUtilityChip } from './components/TariffChips'
import { CreateTariffModal } from './components/CreateTariffModal'
import { CreateUtilityModal } from './components/CreateUtilityModal'
import { EditUtilityModal } from './components/EditUtilityModal'

type TariffRow = TariffItem & { id: string }

const RESULTS_PER_PAGE = 25
const DEFAULT_UTILITY_TYPE: UtilityTypeFilter = 'IOU'
const DEFAULT_SORT_BY = 'utilityShortName'
const DEFAULT_SORT_DIR: 'ASC' | 'DESC' = 'ASC'

const SORTABLE_COLS = [
    'utilityType',
    'utilityShortName',
    'schedule',
    'name',
    'usageCount',
    'rateSheetCount',
] as const

const Tariffs: React.FC = () => {
    const dispatch = useAppDispatch()
    const [searchParams, setSearchParams] = useSearchParams()
    const { tariffs, statusCounts, pendingPastEffectiveCount, listLoading, listError } = useAppSelector(
        (state) => state.tariffs
    )
    const { permissions } = useAppSelector((state) => state.app)
    const canEdit = permissions?.includes('tariffs:update:all') ?? false

    const [iouUtilities, setIouUtilities] = useState<Utility[]>([])
    const [ccaUtilities, setCcaUtilities] = useState<Utility[]>([])
    const [daUtilities, setDaUtilities] = useState<Utility[]>([])
    const [createOpen, setCreateOpen] = useState(false)
    const [createUtilityOpen, setCreateUtilityOpen] = useState(false)
    const [editUtilityOpen, setEditUtilityOpen] = useState(false)
    const [preselectUtility, setPreselectUtility] = useState<Utility | null>(null)

    useEffect(() => {
        void getAdminUtilitiesApi('IOU').then(setIouUtilities)
        void getAdminUtilitiesApi('CCA').then(setCcaUtilities)
        void getAdminUtilitiesApi('ESP_DA').then(setDaUtilities)
    }, [])

    // Default IOU filter and utility sort when the URL omits them.
    useEffect(() => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev)
                let changed = false
                if (next.get('utilityType') == null) {
                    next.set('utilityType', DEFAULT_UTILITY_TYPE)
                    changed = true
                }
                if (next.get('sortBy') == null) {
                    next.set('sortBy', DEFAULT_SORT_BY)
                    changed = true
                }
                if (next.get('sortDir') == null) {
                    next.set('sortDir', DEFAULT_SORT_DIR)
                    changed = true
                }
                return changed ? next : prev
            },
            { replace: true }
        )
    }, [setSearchParams])

    const search = searchParams.get('search') || null
    const status = (searchParams.get('status') as TariffStatus | null) || null
    const utilityType =
        (searchParams.get('utilityType') as UtilityTypeFilter | null) || DEFAULT_UTILITY_TYPE
    const utilityCuid =
        searchParams.get('utilityCuid') ||
        searchParams.get('iouCuid') ||
        searchParams.get('ccaCuid') ||
        null
    const page = parseInt(searchParams.get('page') || '0', 10)
    const sortBy = searchParams.get('sortBy') || DEFAULT_SORT_BY
    const sortDir = (searchParams.get('sortDir') as 'ASC' | 'DESC' | null) || DEFAULT_SORT_DIR

    useEffect(() => {
        sessionStorage.setItem(LIST_SEARCH_KEY, searchParams.toString())
    }, [searchParams])

    const sortingStatus = useMemo(() => {
        const next = new Map<string, string | null>()
        for (const col of SORTABLE_COLS) {
            next.set(col, col === sortBy ? sortDir : null)
        }
        return next
    }, [sortBy, sortDir])

    const utilities = useMemo(() => {
        if (utilityType === 'IOU') return iouUtilities
        if (utilityType === 'CCA') return ccaUtilities
        if (utilityType === 'ESP_DA') return daUtilities
        return [...iouUtilities, ...ccaUtilities, ...daUtilities]
    }, [utilityType, iouUtilities, ccaUtilities, daUtilities])

    const selectedUtility = useMemo(
        () => (utilityCuid == null ? null : utilities.find((utility) => utility.id === utilityCuid) ?? null),
        [utilities, utilityCuid]
    )

    const reload = useCallback(() => {
        return dispatch(
            getTariffs({
                page,
                resultsPerPage: RESULTS_PER_PAGE,
                search: search ?? undefined,
                status: status ?? undefined,
                utilityCuid: utilityCuid ?? undefined,
                utilityType: utilityType === 'ALL' ? undefined : utilityType,
                sortBy,
                sortDir,
            })
        )
    }, [dispatch, page, search, status, utilityCuid, utilityType, sortBy, sortDir])

    useEffect(() => {
        const request = reload()
        return () => {
            request.abort()
        }
    }, [reload])

    const setParam = useCallback(
        (key: string, value: string | null, clearPage = true): void => {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev)
                if (value != null && value !== '') next.set(key, value)
                else next.delete(key)
                if (key === 'utilityCuid' || key === 'utilityType') {
                    next.delete('iouCuid')
                    next.delete('ccaCuid')
                }
                if (clearPage) next.delete('page')
                return next
            })
        },
        [setSearchParams]
    )

    const handleClearFilters = useCallback((): void => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            ;['search', 'status', 'utilityCuid', 'iouCuid', 'ccaCuid', 'page'].forEach((k) =>
                next.delete(k)
            )
            next.set('utilityType', DEFAULT_UTILITY_TYPE)
            next.set('sortBy', DEFAULT_SORT_BY)
            next.set('sortDir', DEFAULT_SORT_DIR)
            return next
        })
    }, [setSearchParams])

    /** Clear search/utility filters, show all utility types, and filter to pending approval. */
    const handleViewWaitingForApproval = useCallback((): void => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            ;['search', 'utilityCuid', 'iouCuid', 'ccaCuid', 'page'].forEach((k) => next.delete(k))
            next.set('utilityType', 'ALL')
            next.set('status', 'PENDING')
            return next
        })
    }, [setSearchParams])

    const handleSort = (columnValue: string, sortDirection: string): void => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            if (sortDirection === 'none' || sortDirection === 'NONE') {
                next.set('sortBy', DEFAULT_SORT_BY)
                next.set('sortDir', DEFAULT_SORT_DIR)
            } else {
                next.set('sortBy', columnValue)
                next.set('sortDir', sortDirection.toUpperCase())
            }
            next.delete('page')
            return next
        })
    }

    const hasActiveFilters =
        search != null ||
        status != null ||
        utilityType !== DEFAULT_UTILITY_TYPE ||
        utilityCuid != null

    const tableData =
        tariffs != null
            ? {
                  ...tariffs,
                  results: tariffs.results.map((t): TariffRow => ({ ...t, id: t.cuid })),
              }
            : { results: [] as TariffRow[], total: 0, page, resultsPerPage: RESULTS_PER_PAGE }


    const pendingApprovalCount = statusCounts?.PENDING ?? statusCounts?.WAITING_FOR_APPROVAL ?? 0
    const showPendingAlert =
        pendingApprovalCount > 0 && status !== 'PENDING' && status !== 'WAITING_FOR_APPROVAL'

    return (
        <Layout
            title={
                <TariffPageHeader
                    showSectionTabs
                    actions={
                        <div className="flex flex-wrap items-center gap-2">
                            {canEdit ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => setCreateUtilityOpen(true)}
                                        className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                                    >
                                        Create utility
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setEditUtilityOpen(true)}
                                        disabled={selectedUtility == null}
                                        className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                                    >
                                        Edit utility
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setCreateOpen(true)}
                                        className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500"
                                    >
                                        Create tariff
                                    </button>
                                </>
                            ) : (
                                <span className="flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400">
                                    <InformationCircleIcon className="h-4 w-4 shrink-0" />
                                    Sufficient permission required to create tariffs.
                                </span>
                            )}
                        </div>
                    }
                />
            }
        >
            <CreateTariffModal
                open={createOpen}
                onClose={() => {
                    setCreateOpen(false)
                    setPreselectUtility(null)
                }}
                iouUtilities={iouUtilities}
                ccaUtilities={ccaUtilities}
                daUtilities={daUtilities}
                preselectUtility={preselectUtility}
                onCreateUtility={() => setCreateUtilityOpen(true)}
            />
            <CreateUtilityModal
                open={createUtilityOpen}
                onClose={() => setCreateUtilityOpen(false)}
                defaultType={utilityType === 'CCA' || utilityType === 'ESP_DA' ? utilityType : 'IOU'}
                onCreated={(utility) => {
                    if (utility.type === 'CCA') {
                        setCcaUtilities((prev) => [...prev, utility])
                    } else if (utility.type === 'ESP_DA') {
                        setDaUtilities((prev) => [...prev, utility])
                    } else {
                        setIouUtilities((prev) => [...prev, utility])
                    }
                    setPreselectUtility(utility)
                }}
            />
            {selectedUtility != null ? (
                <EditUtilityModal
                    open={editUtilityOpen}
                    onClose={() => setEditUtilityOpen(false)}
                    utility={selectedUtility}
                    onSaved={(updated) => {
                        const replace = (prev: Utility[]): Utility[] =>
                            prev.map((item) => (item.id === updated.id ? updated : item))
                        setIouUtilities(replace)
                        setCcaUtilities(replace)
                        setDaUtilities(replace)
                        if (updated.type === 'CCA') {
                            setIouUtilities((prev) => prev.filter((item) => item.id !== updated.id))
                            setDaUtilities((prev) => prev.filter((item) => item.id !== updated.id))
                            setCcaUtilities((prev) =>
                                prev.some((item) => item.id === updated.id) ? replace(prev) : [...prev, updated]
                            )
                        } else if (updated.type === 'ESP_DA') {
                            setIouUtilities((prev) => prev.filter((item) => item.id !== updated.id))
                            setCcaUtilities((prev) => prev.filter((item) => item.id !== updated.id))
                            setDaUtilities((prev) =>
                                prev.some((item) => item.id === updated.id) ? replace(prev) : [...prev, updated]
                            )
                        } else {
                            setCcaUtilities((prev) => prev.filter((item) => item.id !== updated.id))
                            setDaUtilities((prev) => prev.filter((item) => item.id !== updated.id))
                            setIouUtilities((prev) =>
                                prev.some((item) => item.id === updated.id) ? replace(prev) : [...prev, updated]
                            )
                        }
                    }}
                />
            ) : null}

            {showPendingAlert ? (
                <div className="mb-4 flex w-fit flex-wrap items-center gap-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-100">
                    <p>
                        {pendingApprovalCount.toLocaleString()} tariff
                        {pendingApprovalCount === 1 ? '' : 's'} with pending ratesets.
                        {pendingPastEffectiveCount > 0
                            ? ` ${pendingPastEffectiveCount.toLocaleString()} rateset${
                                  pendingPastEffectiveCount === 1 ? '' : 's'
                              } past their effective date.`
                            : ''}
                    </p>
                    <button
                        type="button"
                        onClick={handleViewWaitingForApproval}
                        className="rounded-md bg-blue-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-500"
                    >
                        View
                    </button>
                </div>
            ) : null}

            <TariffFilterBar
                search={search}
                utilityType={utilityType}
                utilityCuid={utilityCuid}
                status={status ?? ''}
                utilities={utilities}
                hasActiveFilters={hasActiveFilters}
                onSearchChange={(value) => setParam('search', value || null)}
                onUtilityTypeChange={(value) => {
                    setSearchParams((prev) => {
                        const next = new URLSearchParams(prev)
                        next.set('utilityType', value)
                        next.delete('utilityCuid')
                        next.delete('iouCuid')
                        next.delete('ccaCuid')
                        next.delete('page')
                        return next
                    })
                }}
                onUtilityCuidChange={(value) => setParam('utilityCuid', value ?? null)}
                onStatusChange={(value) => setParam('status', value === '' ? null : value)}
                onClearFilters={handleClearFilters}
            />

            {listError ? (
                <QueryError message="Failed to load tariffs." onRetry={reload} />
            ) : (
                <Panel header="Tariffs">
                    <Table<TariffRow>
                        isLoading={listLoading}
                        stickyHeader
                        compact
                        scrollShell={{ heightClassName: 'max-h-[calc(100dvh-22rem)]' }}
                        data={tableData}
                        getRowHref={(rec) => `/admin/tariffs/${rec.cuid}`}
                        handlePaginatorClick={(newPage) =>
                            setParam('page', newPage === 0 ? null : String(newPage), false)
                        }
                        onSort={handleSort}
                        sortingStatus={sortingStatus}
                        colConfig={[
                            {
                                label: 'Type',
                                value: 'utilityType',
                                isSortable: true,
                                render: (rec) => {
                                    const type = rec.utilityType
                                    const hint =
                                        type === 'IOU' || type === 'CCA' || type === 'ESP_DA'
                                            ? UTILITY_TYPE_HINTS[type]
                                            : undefined
                                    return <TariffTypeChip type={type} title={hint} />
                                },
                            },
                            {
                                label: 'Utility',
                                value: 'utilityShortName',
                                isSortable: true,
                                render: (rec) => (
                                    <TariffUtilityChip
                                        shortName={rec.utilityShortName}
                                        utilityName={rec.utilityName}
                                        utilityType={rec.utilityType}
                                    />
                                ),
                            },
                            {
                                label: 'Schedule',
                                value: 'schedule',
                                isSortable: true,
                                render: (rec) => (
                                    <span className="font-mono font-normal tracking-tight text-slate-900 dark:text-slate-100">
                                        {rec.schedule}
                                    </span>
                                ),
                            },
                            {
                                label: 'Name',
                                value: 'name',
                                isSortable: true,
                                columnWidth: 360,
                                contentClassName: 'min-w-0',
                                render: (rec) => {
                                    const name = rec.name
                                    if (name == null || name === '') return null
                                    return <span className="block truncate">{name}</span>
                                },
                            },
                            {
                                label: 'Rate Sheets',
                                value: 'rateSheetCount',
                                isSortable: true,
                                render: (rec) =>
                                    rec.rateSheetCount != null && rec.rateSheetCount > 0
                                        ? rec.rateSheetCount.toLocaleString()
                                        : null,
                            },
                            {
                                label: 'Usages',
                                value: 'usageCount',
                                isSortable: true,
                                render: (rec) =>
                                    rec.usageCount != null && rec.usageCount > 0
                                        ? rec.usageCount.toLocaleString()
                                        : null,
                            },
                        ]}
                    />
                </Panel>
            )}
        </Layout>
    )
}

export default Tariffs
export { LIST_SEARCH_KEY }
